import { CACHE_NAME, METADATA_CACHE_NAME, IMAGE_CACHE_NAME } from '../config/cache.config';
import { LOG_DEBUG } from '../config/debug.config';

/**
 * Cache Management Utilities
 *
 * Emergency utilities for managing cache in development/production.
 * These can be called from browser console when cache issues occur.
 */

/**
 * Clear all cache data (emergency reset)
 *
 * Usage in browser console:
 * ```
 * // Clear all Secacam caches
 * clearSecacamCache()
 * ```
 */
export async function clearSecacamCache(): Promise<void> {
  try {
    const cacheNames = await caches.keys();
    const secacamCaches = cacheNames.filter(name => name.includes('secacam') || name.includes('api-cache'));

    console.log('Clearing cache entries:', secacamCaches);

    for (const cacheName of secacamCaches) {
      await caches.delete(cacheName);
      console.log('Deleted cache:', cacheName);
    }

    console.log('All Secacam caches cleared!');
    console.log('Reload the page to start fresh.');
  } catch (error) {
    console.error('Failed to clear cache:', error);
  }
}

/**
 * List all cached URLs
 */
export async function listCachedUrls(): Promise<string[]> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const requests = await cache.keys();
    const urls = requests.map(req => req.url);

    console.log('Cached URLs:', urls);
    return urls;
  } catch (error) {
    console.error('Failed to list cached URLs:', error);
    return [];
  }
}

/**
 * Clear cache for a specific URL pattern
 *
 * @param pattern - String or RegExp to match URLs
 */
export async function clearCacheByPattern(pattern: string | RegExp): Promise<number> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const metadataCache = await caches.open(METADATA_CACHE_NAME);
    const requests = await cache.keys();

    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    let deletedCount = 0;

    for (const request of requests) {
      if (regex.test(request.url)) {
        await cache.delete(request);
        await metadataCache.delete(request);
        deletedCount++;
        console.log('Deleted:', request.url);
      }
    }

    console.log(`Cleared ${deletedCount} cache entries`);
    return deletedCount;
  } catch (error) {
    console.error('Failed to clear cache by pattern:', error);
    return 0;
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{
  totalEntries: number;
  totalSize: number;
}> {
  try {
    const buckets = [CACHE_NAME, METADATA_CACHE_NAME, IMAGE_CACHE_NAME];
    let totalEntries = 0;
    let totalSize = 0;

    for (const bucketName of buckets) {
      const cache = await caches.open(bucketName);
      const requests = await cache.keys();
      totalEntries += requests.length;
      for (const request of requests) {
        const response = await cache.match(request);
        if (response) {
          const sizeHeader = response.headers.get('x-cached-size');
          if (sizeHeader) {
            totalSize += Number(sizeHeader);
          } else {
            const clone = response.clone();
            const blob = await clone.blob();
            totalSize += blob.size;
          }
        }
      }
    }

    const stats = {
      totalEntries,
      totalSize,
    };

    console.log('Cache Statistics (all buckets):', stats);
    console.log(`   Total: ${stats.totalEntries} entries`);
    console.log(`   Size: ${(stats.totalSize / 1024 / 1024).toFixed(2)} MB`);

    return stats;
  } catch (error) {
    console.error('Failed to get cache stats:', error);
    return { totalEntries: 0, totalSize: 0 };
  }
}

/**
 * Validate cache health
 */
export async function validateCacheHealth(): Promise<void> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const metadataCache = await caches.open(METADATA_CACHE_NAME);
    const requests = await cache.keys();

    let corruptedCount = 0;
    let errorCount = 0;

    console.log('Validating cache health...');

    for (const request of requests) {
      const response = await cache.match(request);
      const metadata = await metadataCache.match(request);

      if (!response) {
        console.warn('Missing response for:', request.url);
        corruptedCount++;
        continue;
      }

      if (!metadata) {
        console.warn('Missing metadata for:', request.url);
        corruptedCount++;
        continue;
      }

      // Check if cached response is an error
      if (response.status >= 400) {
        console.warn('Cached error response:', response.status, request.url);
        errorCount++;
        // Auto-delete error responses
        await cache.delete(request);
        await metadataCache.delete(request);
      }
    }

    console.log('Validation complete:');
    console.log(`   Corrupted entries: ${corruptedCount}`);
    console.log(`   Error responses deleted: ${errorCount}`);

    if (corruptedCount > 0 || errorCount > 0) {
      console.warn('Consider running clearSecacamCache() for a fresh start');
    }
  } catch (error) {
    console.error('Failed to validate cache:', error);
  }
}

// Expose utilities to window for console access
if (typeof window !== 'undefined') {
  (window as any).clearSecacamCache = clearSecacamCache;
  (window as any).listCachedUrls = listCachedUrls;
  (window as any).clearCacheByPattern = clearCacheByPattern;
  (window as any).getCacheStats = getCacheStats;
  (window as any).validateCacheHealth = validateCacheHealth;

  if (LOG_DEBUG) {
    console.log('Cache utilities loaded. Available commands:');
    console.log('   - clearSecacamCache()');
    console.log('   - listCachedUrls()');
    console.log('   - clearCacheByPattern(pattern)');
    console.log('   - getCacheStats()');
    console.log('   - validateCacheHealth()');
  }
}
